import { Box, Button, Slider, Typography } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import VideoPlayers from "../../components/ResponseComponents/DialResponse/components/VideoPlayers";
import { FullscreenSlot } from "../../components/Wrappers/FullscreenSlot";
import { questionMediaSurface } from "../../components/ui/QuestionSurface/styles";
import { useThemeContext } from "../../globalContext/global-context";
import type { VideoCustomMessage } from "../../util/types";
import type { QuestionViewProps } from "../contracts";
import type { VideoDefinition, VideoDraft, VideoEvent } from "./core";
import {
  setupVideoPlaybackHandlers,
  setupVideoVisibilityHandlers,
} from "./playback";
import {
  dialCaptionStyle,
  dialControlsStyle,
  dialSliderStyle,
  dialSliderWrapperStyle,
  videoMediaStyle,
  videoPlayerChildrenStyle,
  videoPlayerWrapperStyle,
} from "./styles";

type ViewProps = QuestionViewProps<VideoDefinition, VideoDraft, VideoEvent>;

export const VideoView = (props: ViewProps) => {
  const { globalObject } = useThemeContext();
  const theme = globalObject.themeData.normalizedTheme;
  const playerRef = useRef<HTMLElement>(null);
  const wasPlayingRef = useRef(false);
  const dispatchRef = useRef(props.dispatch);
  dispatchRef.current = props.dispatch;
  const completeVideoRef = useRef(props.completeVideo);
  completeVideoRef.current = props.completeVideo;
  const [playing, setPlaying] = useState(false);
  const [offsetSeconds, setOffsetSeconds] = useState(0);
  const { definition, draft } = props;
  const dialValueRef = useRef(draft.dialValue);
  dialValueRef.current = draft.dialValue;
  const dial = definition.dial;
  const isFullscreen = playing && !draft.ended;
  const message = useMemo<VideoCustomMessage>(
    () => ({
      type: "video",
      content: definition.prompt,
      self: false,
      createdAt: new Date(0),
      video: definition.videoUrl,
      style: definition.style,
      start_message: definition.startMessage,
      post_data: definition.postData,
      ...(definition.dial
        ? {
            options: definition.dial.points.map((point) => ({
              value: point.value,
              text: point.label,
              display_text: true,
            })),
            playing_message: definition.dial.caption,
            default: definition.dial.initialValue,
          }
        : {}),
    }),
    [definition],
  );

  useEffect(() => {
    if (draft.ended) return;
    return setupVideoPlaybackHandlers({
      devRef: playerRef,
      message,
      setOffSetSeconds: setOffsetSeconds,
      videoEndListener: (terminalOffsetSeconds) => {
        dispatchRef.current({ type: "end" });
        const completeVideo = completeVideoRef.current;
        if (!completeVideo) return;
        void completeVideo(
          dialValueRef.current ?? "",
          terminalOffsetSeconds,
          definition.postData,
        ).then((completed) => {
          dispatchRef.current({
            type: completed ? "completion-ready" : "completion-failed",
          });
        });
      },
      applyFullscreenStyles: () => setPlaying(true),
      startPositionSeconds: definition.startPositionSeconds,
      endPositionSeconds: definition.endPositionSeconds,
    });
  }, [draft.ended, message]);
  useEffect(() => {
    if (draft.ended) return;
    return setupVideoVisibilityHandlers({
      devRef: playerRef,
      wasPlayingRef,
      startPositionSeconds: definition.startPositionSeconds,
    });
  }, [draft.ended, message]);

  const dialMin = dial
    ? Math.min(...dial.points.map((point) => point.value))
    : 0;
  const dialMax = dial
    ? Math.max(...dial.points.map((point) => point.value))
    : 100;
  const dialOrientation =
    dial?.position === "right" ? "vertical" : "horizontal";
  const slider =
    dial && playing && !draft.ended ? (
      <Box sx={dialControlsStyle(dial.position, props.display.arrangement)}>
        {dial.position === "right" && dial.caption && (
          <Typography sx={dialCaptionStyle(theme)}>{dial.caption}</Typography>
        )}
        <Box sx={dialSliderWrapperStyle(dialOrientation)}>
          <Slider
            aria-label={theme.copy.labels.videoDial}
            data-testid="video-dial-slider"
            size="small"
            valueLabelDisplay="on"
            orientation={dialOrientation}
            min={dialMin}
            max={dialMax}
            step={1}
            marks={dial.points.map((point) => ({
              value: point.value,
              label: point.label,
            }))}
            value={draft.dialValue ?? dial.initialValue}
            onChange={(_, value) => {
              if (typeof value !== "number") return;
              props.dispatch({ type: "set-dial", value });
              props.sampleDial?.(value, offsetSeconds, definition.postData);
            }}
            sx={dialSliderStyle(theme, dialOrientation, dial.points.length)}
          />
        </Box>
      </Box>
    ) : null;

  return (
    <Box
      data-testid="video-view"
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: `${theme.tokens.spacing["4"]}px`,
      }}
    >
      <FullscreenSlot
        active={isFullscreen}
        ariaLabel={theme.copy.labels.videoPlayback}
        testId="video-fullscreen-slot"
      >
        <Box
          data-question-media
          sx={[
            questionMediaSurface(theme),
            videoMediaStyle(
              theme,
              isFullscreen,
              dial?.position,
              props.display.arrangement,
            ),
          ]}
        >
          {draft.ended ? (
            <Box
              data-testid="video-ended"
              sx={{
                minHeight: 275,
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: `${theme.tokens.spacing["4"]}px`,
                padding: `${theme.tokens.spacing["6"]}px`,
                textAlign: "center",
                color: theme.tokens.color.text.primary,
                fontSize: theme.tokens.typography.size.lg,
              }}
            >
              <span>{definition.prompt}</span>
              {draft.completionFailed && (
                <Button
                  variant="contained"
                  onClick={() => {
                    setPlaying(false);
                    props.dispatch({ type: "retry" });
                  }}
                >
                  {theme.copy.actions.retryVideo}
                </Button>
              )}
            </Box>
          ) : (
            <>
              {isFullscreen && dial?.position === "bottom" && dial.caption && (
                <Typography sx={dialCaptionStyle(theme)}>
                  {dial.caption}
                </Typography>
              )}
              <Box
                ref={playerRef}
                sx={[
                  videoPlayerWrapperStyle(
                    isFullscreen,
                    dial?.position,
                    props.display.arrangement,
                  ),
                  videoPlayerChildrenStyle(isFullscreen),
                ]}
              >
                {VideoPlayers(
                  definition.videoUrl,
                  {
                    width: "100%",
                    height: isFullscreen ? "100%" : undefined,
                    aspectRatio: isFullscreen ? undefined : "16 / 9",
                    objectFit: isFullscreen ? "contain" : undefined,
                    borderRadius: isFullscreen
                      ? 0
                      : theme.tokens.shape.radius.lg,
                  },
                  theme.copy,
                )}
              </Box>
              {slider}
            </>
          )}
        </Box>
      </FullscreenSlot>
      {!draft.ended && !playing && definition.startMessage ? (
        <Typography
          variant="h6"
          sx={{ color: theme.tokens.color.action.primary }}
          data-question-notice
        >
          {definition.startMessage}
        </Typography>
      ) : null}
    </Box>
  );
};
